import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../../../authService';
import UserManagement from './pages/UserManagement';
import DepartmentManagement from './pages/DepartmentManagement';
import OfflineBackup from './pages/OfflineBackup';
import MobileBottomBar from '../../common/MobileBottomBar';
import ProfileModal from '../../modals/ProfileModal';
import UnifiedSidebar from '../../common/UnifiedSidebar';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  useTheme,
  useMediaQuery,
  CssBaseline
} from '@mui/material';
import {
  Menu as MenuIcon,
  People,
  Business,
  CloudDownload,
  ArrowBack,
  Settings,
  Person
} from '@mui/icons-material';

const drawerWidth = 280;

const SettingsDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [user, setUser] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);

  useEffect(() => {
    const currentUser = authService.getUserSession();
    if (!currentUser) {
      navigate('/login');
    } else {
      setUser(currentUser);
    }
  }, [navigate]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    authService.logout();
    navigate('/login', { replace: true });
  };

  const menuItems = [
    { name: 'Users', icon: <People />, path: '/portal/settings/users' },
    { name: 'Depts', icon: <Business />, path: '/portal/settings/departments' },
    { name: 'Backup', icon: <CloudDownload />, path: '/portal/settings/offline' }
  ];

  const drawer = (
    <UnifiedSidebar
      user={user}
      onLogout={handleLogout}
      onCloseMobile={() => setMobileOpen(false)}
      onOpenProfile={() => setShowProfileModal(true)}
    />
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      
      {/* Mobile Top AppBar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          display: { md: 'none' },
          bgcolor: '#ffffff',
          color: '#0f172a',
          borderBottom: '1px solid #e2e8f0'
        }}
      >
        <Toolbar sx={{ minHeight: 56 }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 1.5, color: '#0f172a' }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Settings sx={{ color: '#059669', fontSize: 22 }} />
            <Typography variant="subtitle1" fontWeight={800} color="#0f172a" noWrap>
              System Settings
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Side Navigation Drawers */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, borderRight: '1px solid #e2e8f0' },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, borderRight: '1px solid #e2e8f0' },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          pb: { xs: 10, md: 4 },
          width: { md: `calc(100% - ${drawerWidth}px)` },
          bgcolor: '#f8faf9',
          minHeight: '100vh',
          mt: { xs: 7, md: 0 }
        }}
      >
        <Routes>
          <Route path="users" element={<UserManagement user={user} />} />
          <Route path="departments" element={<DepartmentManagement user={user} />} />
          <Route path="offline" element={<OfflineBackup user={user} />} />
          <Route path="*" element={<UserManagement user={user} />} />
        </Routes>
      </Box>

      {/* Profile Modal */}
      {showProfileModal && (
        <ProfileModal
          open={showProfileModal}
          onClose={() => setShowProfileModal(false)}
          user={user}
          onProfileUpdate={(updatedUser) => {
            setUser(updatedUser);
            authService.setUserSession(updatedUser);
          }}
        />
      )}

      {/* Sticky Mobile Bottom Navigation */}
      <MobileBottomBar
        items={menuItems}
        currentPath={location.pathname}
        onNavigate={(path) => navigate(path)}
        onToggleMenu={handleDrawerToggle}
        isMenuOpen={mobileOpen}
      />
    </Box>
  );
};

export default SettingsDashboard;
